import type { EnvelopeSourceConfig } from './create-envelope-source';
import { AVERAGE_UNAVAILABLE, average, daysInPeriod } from '../averages';
import type {
    KpiCardDataRequest,
    KpiCardState,
    KpiMetric,
    KpiSource,
    KpiSourceDeps,
    ResolvedPeriod,
} from '../types';
import type { EnvelopeCaveat } from '~stores/kpi-envelope';

/** Shared with `withCampaignRateWindows`: the same page, the same missing
 * campaign, so the same sentence. */
export const NO_CAMPAIGN_WINDOW =
    'No Road to Rewards campaign is configured, so there is no campaign window.';

const SELECTED_WINDOW_FAILED =
    'The selected period could not be read, so its two averages are unavailable.';

/** A window that has not answered yet is not a window that failed: the card
 * waits as a whole rather than settling on a degraded trio. */
const hasFailed = (state: KpiCardState): boolean =>
    state.status === 'unavailable' || state.status === 'error';

type WindowTotal =
    | {
          served: true;
          value: number;
          /** The window the API answered for, which a preset leaves to it. */
          period: ResolvedPeriod;
          isCurrency?: boolean;
          caveats?: EnvelopeCaveat[];
          computedAt?: string;
      }
    | { served: false; state: KpiCardState };

/** A figure is only as fresh as its oldest input, the rule the API's report
 * exports follow for `dataAsOf`. Spreads to nothing when no window has a date. */
export const oldestComputedAt = (
    ...dates: (string | undefined)[]
): { computedAt?: string } => {
    const known = dates.filter((date): date is string => date !== undefined);
    if (known.length === 0) return {};
    return {
        computedAt: known.reduce((oldest, date) =>
            Date.parse(date) < Date.parse(oldest) ? date : oldest
        ),
    };
};

/** A window's total, read through the envelope source's own `number` path. */
const windowTotal = async (
    base: KpiSource,
    req: KpiCardDataRequest,
    period: ResolvedPeriod,
    deps: KpiSourceDeps
): Promise<WindowTotal> => {
    const state = await base.getCardData(
        { ...req, form: 'number', period, compare: undefined },
        deps
    );
    if (state.status !== 'ready') {
        return { served: false, state };
    }
    if (state.data.form !== 'number') {
        return { served: false, state: { status: 'error' } };
    }
    return {
        served: true,
        value: state.data.value,
        period: state.served?.range ?? period,
        ...(state.data.isCurrency && { isCurrency: true }),
        ...(state.caveats && { caveats: state.caveats }),
        ...(state.computedAt !== undefined && {
            computedAt: state.computedAt,
        }),
    };
};

export interface CampaignSourceConfig<M extends KpiMetric = KpiMetric>
    extends EnvelopeSourceConfig<M> {
    /** When true, every non-trio form this source serves reads the
     * campaign-to-date window instead of the page's selected period. Opt-in per
     * source, not per form. */
    alwaysCampaignToDate?: boolean;
    /** Metric ids whose trio never computes a window average: an intensive
     * ratio's denominator grows with the window as fast as its numerator, so
     * the selected window is skipped; both averages read the sentinel instead. */
    intensiveMetrics?: readonly NoInfer<M>[];
    /** Metric ids whose trio figures are a 0..1 rate: flagged `isPercent` so
     * the renderer scales and suffixes it, mirroring `isCurrency`'s `$`
     * prefix. Left unscaled here so the multiply-by-100 happens once. */
    percentMetrics?: readonly NoInfer<M>[];
}

/**
 * Wraps a source so it also serves `trio`, where one card reads two windows.
 * `KpiCardSpec.period` pins a card all or nothing, so the second window is
 * fetched here instead.
 */
export const withCampaignWindows = <M extends KpiMetric>(
    base: KpiSource<M>,
    config: CampaignSourceConfig<M>
): KpiSource<M> => {
    // Widened once here (readonly arrays are covariant): `req.metric` below
    // is the broad `KpiMetric`, not this source's own narrower `M`.
    const intensiveMetrics: readonly KpiMetric[] | undefined =
        config.intensiveMetrics;
    const percentMetrics: readonly KpiMetric[] | undefined =
        config.percentMetrics;

    return {
        descriptor: base.descriptor,

        async getCardData(
            req: KpiCardDataRequest,
            deps: KpiSourceDeps
        ): Promise<KpiCardState> {
            if (req.form !== 'trio') {
                if (!config.alwaysCampaignToDate) {
                    return base.getCardData(req, deps);
                }
                if (!req.campaignToDate) {
                    return {
                        status: 'unavailable',
                        reason: NO_CAMPAIGN_WINDOW,
                    };
                }
                return base.getCardData(
                    { ...req, period: req.campaignToDate },
                    deps
                );
            }
            if (!req.campaignToDate) {
                return { status: 'unavailable', reason: NO_CAMPAIGN_WINDOW };
            }

            const isPercent =
                req.metric != null && percentMetrics?.includes(req.metric);

            // An intensive metric has no window average at any width, so the
            // selected window is never fetched for one: there is nothing its
            // value would contribute.
            if (req.metric != null && intensiveMetrics?.includes(req.metric)) {
                const toDate = await windowTotal(
                    base,
                    req,
                    req.campaignToDate,
                    deps
                );
                if (!toDate.served) return toDate.state;
                return {
                    status: 'ready',
                    data: {
                        form: 'trio',
                        toDate: toDate.value,
                        weekly: AVERAGE_UNAVAILABLE,
                        daily: AVERAGE_UNAVAILABLE,
                        ...(toDate.isCurrency && { isCurrency: true }),
                        ...(isPercent && { isPercent: true }),
                    },
                    ...(toDate.caveats && { caveats: toDate.caveats }),
                    ...oldestComputedAt(toDate.computedAt),
                };
            }

            const [toDate, selected] = await Promise.all([
                windowTotal(base, req, req.campaignToDate, deps),
                windowTotal(base, req, req.period, deps),
            ]);
            if (!toDate.served) return toDate.state;
            // The to-date total is the one number the page Period cannot move.
            // A selected window that fails takes down the two averages it feeds,
            // not the total beside them.
            if (!selected.served) {
                if (!hasFailed(selected.state)) return selected.state;
                return {
                    status: 'ready',
                    data: {
                        form: 'trio',
                        toDate: toDate.value,
                        weekly: AVERAGE_UNAVAILABLE,
                        daily: AVERAGE_UNAVAILABLE,
                        ...(toDate.isCurrency && { isCurrency: true }),
                        ...(isPercent && { isPercent: true }),
                    },
                    caveats: [
                        ...(toDate.caveats ?? []),
                        {
                            kind: 'warning',
                            code: 'selected_period_unavailable',
                            text:
                                selected.state.status === 'unavailable'
                                    ? selected.state.reason
                                    : SELECTED_WINDOW_FAILED,
                        },
                    ],
                    ...oldestComputedAt(toDate.computedAt),
                };
            }

            const { weekly, daily } = average(
                selected.value,
                daysInPeriod(selected.period)
            );
            return {
                status: 'ready',
                data: {
                    form: 'trio',
                    toDate: toDate.value,
                    weekly,
                    daily,
                    ...(toDate.isCurrency && { isCurrency: true }),
                    ...(isPercent && { isPercent: true }),
                },
                // The selected window's notes, not the campaign's: they qualify
                // the window the reader chose.
                ...(selected.caveats && { caveats: selected.caveats }),
                ...oldestComputedAt(toDate.computedAt, selected.computedAt),
            };
        },
    };
};
